import React, { useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSSVariableValue } from '../../../assets/ts/_utils'
import { useThemeMode } from '../../layout/theme-mode/ThemeModeProvider'
import { useGetUserActivity } from "../../../api/hooks"
import { IUserActivities } from "../../../models"
import { IFormValue } from "../../../../app/pages/dashboard/DashboardWrapper"

type Props = {
  className: string;
  chartColor: string;
  chartHeight: string;
  formValue: IFormValue;
}

const MixedWidget11: React.FC<Props> = ({ className, chartColor, chartHeight, formValue }) => {
  const { data, isLoading } = useGetUserActivity({
    activity_per_user_deciles_from: formValue.fromDate,
    activity_per_user_deciles_to: formValue.toDate,
    top_active_users_from: formValue.fromDate,
    top_active_users_to: formValue.toDate
  })
  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartInstance = useRef<ApexCharts | null>(null)
  const { mode } = useThemeMode()

  useEffect(() => {
    if (!chartRef.current) return

    // create chart instance only once
    if (!chartInstance.current) {
      chartInstance.current = new ApexCharts(
          chartRef.current,
          chartOptions(chartColor, chartHeight, data)
      )
      chartInstance.current.render()
    } else if (data) {
      // update existing chart with new data
      chartInstance.current.updateOptions(chartOptions(chartColor, chartHeight, data))
    }
  }, [data, mode, chartColor, chartHeight])

  useEffect(() => {
    return () => {
      // cleanup on unmount
      if (chartInstance.current) {
        chartInstance.current.destroy()
        chartInstance.current = null
      }
    }
  }, [])

  return (
      <div className={`card ${className}`}>
        <div className="h-25">
          <h3 className="fw-bold text-gray-800 w-100 h-50px p-4 rounded-top bg-light-primary">
            User Engagement
          </h3>
        </div>

        <div className="card-body p-0 d-flex justify-content-between flex-column overflow-hidden">
          <div className="d-flex flex-stack flex-wrap flex-grow-1 px-9 pt-9 pb-3">
            <div className="me-2">
              <span className="fw-bold text-gray-800 d-block fs-3">Activity Per User (Monthly)</span>
              <span className="text-gray-400 fw-semibold">
              month: {data?.activity_per_user?.month}
            </span>
            </div>
            <div className={`fw-bold fs-3 text-${chartColor}`}>
              {data?.activity_per_user?.count}
            </div>
          </div>

          <div className="position-relative flex-grow-1">
            {isLoading ? (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
            ) : (
                <div ref={chartRef} className="mixed-widget-10-chart"></div>
            )}
          </div>
        </div>
      </div>
  )
}

const chartOptions = (chartColor: string, chartHeight: string, data?: IUserActivities): ApexOptions => {
  const labelColor = getCSSVariableValue('--kt-gray-500')
  const borderColor = getCSSVariableValue('--kt-gray-200')
  const baseColor = getCSSVariableValue('--kt-' + chartColor)

  return {
    series: [
      {
        name: 'User Count',
        type: 'column',
        data: data ? data?.activity_per_user_deciles?.map(i => i?.user_count) : [],
      },
      {
        name: 'Average Activity Count',
        type: 'line',
        data: data ? data?.activity_per_user_deciles?.map(i => i?.average_activity_count) : [],
      },
    ],
    chart: {
      id: 'user-activity-chart',
      fontFamily: 'inherit',
      type: 'line',
      height: chartHeight,
      toolbar: { show: false },
    },
    stroke: { width: [0, 3] },
    plotOptions: {
      bar: { horizontal: false, columnWidth: '50%', borderRadius: 5 },
    },
    xaxis: {
      categories: data
          ? data?.activity_per_user_deciles?.map((d, i) => `Decile ${d?.decile}`)
          : [],
      labels: { style: { colors: labelColor, fontSize: '12px' } },
    },
    yaxis: [
      {
        title: { text: 'User Count' },
        labels: { style: { colors: labelColor, fontSize: '12px' } },
      },
      {
        opposite: true,
        title: { text: 'Avg Activity Count' },
        labels: { style: { colors: '#FB8C00', fontSize: '12px' } },
      },
    ],
    tooltip: { shared: true, intersect: false, style: { fontSize: '12px' } },
    colors: [baseColor, '#FB8C00'],
    legend: { position: 'top' },
    grid: { borderColor: borderColor, strokeDashArray: 4, yaxis: { lines: { show: true } } },
  }
}

export { MixedWidget11 }
